// ─────────────────────────────────────────────────────────────────────────────
// Quarterly OKR live review — socket store for `/quarterly-okr-live-review`
// (§10). Same shape as `AnnualOkrLiveReviewSocketStore`: a vanilla store the
// screen connects on mount and hard-disconnects on leave (the backend derives
// presence from the connection, so a lingering socket would lie about who is
// in the room).
//
//   • `qokr_lr_details` is the whole truth and is re-sent to the room after
//     every change — it REPLACES `details`, never merged (§18.2);
//   • every action is an emit with an ack; refusals arrive as `error`;
//   • reconnects re-join, which brings a fresh snapshot.
//
// ⚠️ THIS FILE IS IDENTICAL IN BOTH REPOS — `10x-coach-frontend`'s
// `CoachPanel/QuarterlyOkr/QuarterlyOkrLiveReview/_socket/` holds the same
// bytes at the same folder depth. The CEO and the coach read one room; any
// change here goes to both copies in the same pass (`diff` is the test).
// ─────────────────────────────────────────────────────────────────────────────

import { createStore } from "zustand/vanilla";
import { useEffect, useState } from "react";
import { unifiedProjectSocketManager } from "../../../../../socket";
import { errorNoti } from "../../../../../base/BasicNotifications";
import { ERROR_COPY } from "../../_config/quarterlyOkrConfig";

export const QOKR_LR_NAMESPACE = "quarterly-okr-live-review";

export const QOKR_LR_EMIT = {
  JOIN: "qokr_lr_join",
  SAVE_OBJECTIVE: "qokr_lr_save_objective",
  DELETE_PREVIEW: "qokr_lr_delete_preview",
  DELETE_OBJECTIVE: "qokr_lr_delete_objective",
  SET_REVIEW_STATUS: "qokr_lr_set_review_status",
  MARK_DISCUSSED: "qokr_lr_mark_discussed",
  DRAFT_MEMBER: "qokr_lr_draft_member",
  LOCK_PREFLIGHT: "qokr_lr_lock_preflight",
  LOCK: "qokr_lr_lock",
};

export const QOKR_LR_ON = {
  DETAILS: "qokr_lr_details",
  PRESENCE: "presence_update",
  MEMBER_DRAFT_READY: "qokr_lr_member_draft_ready",
  LOCKED: "qokr_lr_locked",
  STARTED: "qokr_live_review_started",
  ERROR: "error",
};

// `your_role` (§10): editors change OKRs; members watch.
export const LR_ROLE = { CEO: "ceo", COACH: "coach", MEMBER: "member" };
export const isEditorRole = (role) =>
  role === LR_ROLE.CEO || role === LR_ROLE.COACH;

// Codes the screen reacts to itself (navigation / state), so not toasted.
const SILENT_CODES = ["live_review_not_started"];

const INITIAL_STATE = {
  uniqueId: null,
  connected: false,
  joined: false,
  role: null,
  details: null,
  presence: [],
  locked: null, // `qokr_lr_locked { locked_at, locked_by }`
  notStarted: false, // join refused with `live_review_not_started`
  draftingUserIds: [], // "Draft with AI" in flight
  pending: {},
};

const parse = (raw) => {
  try {
    return typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }
};

const getSocket = () =>
  unifiedProjectSocketManager.getSocket(QOKR_LR_NAMESPACE);

const quarterlyOkrLiveReviewSocketStore = createStore((set, get) => {
  // Emit with an ack, flagging `pending[key]` until the server answers.
  // Resolves the ack (`{ acknowledgement, ...data }`), never rejects.
  const emit = (key, event, payload) =>
    new Promise((resolve) => {
      const socket = getSocket();
      if (!socket?.connected) {
        errorNoti(ERROR_COPY.not_connected);
        resolve({ acknowledgement: false });
        return;
      }
      set((s) => ({ pending: { ...s.pending, [key]: true } }));
      socket.emit(event, payload, (ack) => {
        set((s) => {
          const pending = { ...s.pending };
          delete pending[key];
          return { pending };
        });
        resolve(ack || { acknowledgement: false });
      });
    });

  const handlers = {
    connect: () => {
      set({ connected: true });
      get().join();
    },
    disconnect: () => set({ connected: false }),
    [QOKR_LR_ON.DETAILS]: (raw) => {
      const details = parse(raw);
      if (!details) return;
      set((s) => ({
        details,
        presence: details.presence || s.presence,
        role: details.your_role || s.role,
        joined: true,
      }));
    },
    [QOKR_LR_ON.PRESENCE]: (raw) =>
      set({ presence: parse(raw)?.participants || [] }),
    [QOKR_LR_ON.MEMBER_DRAFT_READY]: (raw) => {
      const userId = String(parse(raw)?.user_id);
      set((s) => ({
        draftingUserIds: s.draftingUserIds.filter((id) => id !== userId),
      }));
    },
    [QOKR_LR_ON.LOCKED]: (raw) => set({ locked: parse(raw) || {} }),
    [QOKR_LR_ON.ERROR]: (raw) => {
      const error = parse(raw) || {};
      if (error.code === "live_review_not_started") set({ notStarted: true });
      // `stale_card`: a fresh snapshot is already on its way to us.
      if (!SILENT_CODES.includes(error.code)) {
        errorNoti(ERROR_COPY[error.code] || error.message);
      }
    },
  };

  return {
    ...INITIAL_STATE,

    connect: (uniqueId) => {
      const socket = unifiedProjectSocketManager.connect(QOKR_LR_NAMESPACE);
      if (!socket) return;
      set({ ...INITIAL_STATE, uniqueId });
      Object.entries(handlers).forEach(([event, handler]) => {
        socket.off(event, handler);
        socket.on(event, handler);
      });
      if (socket.connected) handlers.connect();
    },

    disconnect: () => {
      const socket = getSocket();
      if (socket) {
        Object.entries(handlers).forEach(([event, handler]) =>
          socket.off(event, handler),
        );
      }
      unifiedProjectSocketManager.hardDisconnect(QOKR_LR_NAMESPACE);
      set(INITIAL_STATE);
    },

    // The ack carries the role; the snapshot follows as `qokr_lr_details`.
    join: () =>
      emit("join", QOKR_LR_EMIT.JOIN, { unique_id: get().uniqueId }).then(
        (ack) => {
          if (ack?.acknowledgement && ack.role) set({ role: ack.role });
          return ack;
        },
      ),

    // `objectiveId` absent = create. `version` from the last snapshot.
    saveObjective: (payload) =>
      emit(
        `save:${payload.type}:${payload.objective_id || "new"}`,
        QOKR_LR_EMIT.SAVE_OBJECTIVE,
        payload,
      ),

    deletePreview: (type, objectiveId) =>
      emit(`delete:${type}:${objectiveId}`, QOKR_LR_EMIT.DELETE_PREVIEW, {
        type,
        objective_id: objectiveId,
      }),

    deleteObjective: (type, objectiveId) =>
      emit(`delete:${type}:${objectiveId}`, QOKR_LR_EMIT.DELETE_OBJECTIVE, {
        type,
        objective_id: objectiveId,
      }),

    setReviewStatus: (type, objectiveId, status) =>
      emit(`status:${type}:${objectiveId}`, QOKR_LR_EMIT.SET_REVIEW_STATUS, {
        type,
        objective_id: objectiveId,
        status,
      }),

    markDiscussed: (type, objectiveId) =>
      emit(`discussed:${type}:${objectiveId}`, QOKR_LR_EMIT.MARK_DISCUSSED, {
        type,
        objective_id: objectiveId,
      }),

    // Acks at once; `qokr_lr_member_draft_ready` arrives when written.
    draftMember: (userId) => {
      const id = String(userId);
      set((s) => ({ draftingUserIds: [...s.draftingUserIds, id] }));
      return emit(`draft:${id}`, QOKR_LR_EMIT.DRAFT_MEMBER, {
        user_id: id,
      }).then((ack) => {
        if (!ack?.acknowledgement) {
          set((s) => ({
            draftingUserIds: s.draftingUserIds.filter((x) => x !== id),
          }));
        }
        return ack;
      });
    },

    lockPreflight: () => emit("lockPreflight", QOKR_LR_EMIT.LOCK_PREFLIGHT, {}),
    lock: () => emit("lock", QOKR_LR_EMIT.LOCK, {}),
  };
});

// Same hand-rolled selector hook as the other QOKR stores.
export const useQuarterlyOkrLiveReviewStore = (selector) => {
  const [state, setState] = useState(() =>
    selector(quarterlyOkrLiveReviewSocketStore.getState()),
  );
  useEffect(
    () =>
      quarterlyOkrLiveReviewSocketStore.subscribe((s) => {
        const next = selector(s);
        setState((prev) => (prev === next ? prev : next));
      }),
    [], // eslint-disable-line
  );
  return state;
};

export default quarterlyOkrLiveReviewSocketStore;
